import React, { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  CheckCircle2,
  Send,
} from "lucide-react";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_bbekbvl";
const EMAILJS_TEMPLATE_ID = "template_hb600oj";
const EMAILJS_PUBLIC_KEY = "kP6WHDC83dNyORhHW";

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MOIS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

// Generate time slots from 8h to 19h by 30min
const generateSlots = () => {
  const slots = [];
  for (let h = 8; h < 19; h++) {
    slots.push(`${String(h).padStart(2, "0")}:00`);
    slots.push(`${String(h).padStart(2, "0")}:30`);
  }
  slots.push("19:00");
  return slots;
};

const ALL_SLOTS = generateSlots();

// Deterministic pseudo-random based on date string
const seededRandom = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return () => {
    hash = (hash * 16807) % 2147483647;
    return (hash & 0x7fffffff) / 0x7fffffff;
  };
};

// Check if a slot is in business hours (8h-12h or 13h30-17h)
const isBusinessHour = (slot) => {
  const [h, m] = slot.split(":").map(Number);
  const minutes = h * 60 + m;
  return (minutes >= 480 && minutes < 720) || (minutes >= 810 && minutes < 1020);
};

// Generate "busy" slots for a given date (70% of business hours)
const getBusySlots = (dateStr) => {
  const rng = seededRandom(dateStr);
  const businessSlots = ALL_SLOTS.filter(isBusinessHour);
  const busy = new Set();
  for (const slot of businessSlots) {
    if (rng() < 0.7) {
      busy.add(slot);
    }
  }
  return busy;
};

// localStorage helpers
const STORAGE_KEY = "keyvest_booked_slots";

const getBookedSlots = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveBookedSlot = (dateStr, slot) => {
  const booked = getBookedSlots();
  if (!booked[dateStr]) booked[dateStr] = [];
  booked[dateStr].push(slot);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(booked));
};

const RendezVousPage = () => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState(1); // 1=calendar, 2=slots, 3=form, 4=confirmation
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    message: "",
  });


  // Calendar logic
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  // Convert Sunday=0 to Monday-based (Mon=0)
  const startDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const isPastDay = (day) => {
    const d = new Date(currentYear, currentMonth, day);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t;
  };

  const isWeekend = (day) => {
    const d = new Date(currentYear, currentMonth, day).getDay();
    return d === 0 || d === 6;
  };

  const isToday = (day) => {
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };

  const canGoPrev = () => {
    return currentYear > today.getFullYear() ||
      (currentYear === today.getFullYear() && currentMonth > today.getMonth());
  };

  const navigateMonth = (dir) => {
    if (dir === -1 && !canGoPrev()) return;
    const newMonth = currentMonth + dir;
    if (newMonth > 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else if (newMonth < 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(newMonth);
    }
  };

  const selectDate = (day) => {
    if (isPastDay(day) || isWeekend(day)) return;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setStep(2);
  };

  // Slot logic
  const busySlots = useMemo(() => {
    if (!selectedDate) return new Set();
    return getBusySlots(selectedDate);
  }, [selectedDate]);

  const bookedForDate = useMemo(() => {
    if (!selectedDate) return [];
    const booked = getBookedSlots();
    return booked[selectedDate] || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, step]);

  const isSlotAvailable = useCallback(
    (slot) => {
      if (busySlots.has(slot)) return false;
      if (bookedForDate.includes(slot)) return false;
      // If today, hide past slots
      if (selectedDate) {
        const [y, m, d] = selectedDate.split("-").map(Number);
        const now = new Date();
        if (y === now.getFullYear() && m === now.getMonth() + 1 && d === now.getDate()) {
          const [sh, sm] = slot.split(":").map(Number);
          if (sh < now.getHours() || (sh === now.getHours() && sm <= now.getMinutes())) {
            return false;
          }
        }
      }
      return true;
    },
    [busySlots, bookedForDate, selectedDate]
  );

  const selectSlot = (slot) => {
    if (!isSlotAvailable(slot)) return;
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatDateFr = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const joursSemaine = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    return `${joursSemaine[date.getDay()]} ${d} ${MOIS[m - 1]} ${y}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const templateParams = {
      nom: formData.nom,
      prenom: formData.prenom,
      email: formData.email,
      telephone: formData.telephone,
      date: formatDateFr(selectedDate),
      heure: selectedSlot,
      message: formData.message || "Aucun message",
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      // Save to localStorage
      saveBookedSlot(selectedDate, selectedSlot);
      setStep(4);
    } catch (err) {
      console.error("EmailJS error:", err);
      alert("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedSlot(null);
    setFormData({ nom: "", prenom: "", email: "", telephone: "", message: "" });
  };

  // Render calendar grid
  const renderCalendar = () => {
    const cells = [];
    // Empty cells before first day
    for (let i = 0; i < startDay; i++) {
      cells.push(<div key={`empty-${i}`} />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const past = isPastDay(day);
      const weekend = isWeekend(day);
      const disabled = past || weekend;
      const todayClass = isToday(day);
      const selected =
        selectedDate ===
        `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      cells.push(
        <button
          key={day}
          onClick={() => !disabled && selectDate(day)}
          disabled={disabled}
          className={`aspect-square rounded-xl text-sm font-medium transition-all duration-200 ${
            disabled
              ? "text-[#1A1A2E]/20 cursor-not-allowed"
              : selected
              ? "bg-[#C4956A] text-white shadow-md"
              : todayClass
              ? "bg-[#C4956A]/15 text-[#C4956A] font-bold hover:bg-[#C4956A]/25"
              : "text-[#1A1A2E] hover:bg-[#E8DDD3]/50 cursor-pointer"
          }`}
        >
          {day}
        </button>
      );
    }
    return cells;
  };

  // Render time slots
  const renderSlots = () => {
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {ALL_SLOTS.map((slot) => {
          const available = isSlotAvailable(slot);
          const selected = selectedSlot === slot;
          return (
            <button
              key={slot}
              onClick={() => available && selectSlot(slot)}
              disabled={!available}
              className={`py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                !available
                  ? "bg-[#1A1A2E]/5 text-[#1A1A2E]/25 cursor-not-allowed"
                  : selected
                  ? "bg-[#C4956A] text-white shadow-md"
                  : "bg-[#FAF9F6] text-[#1A1A2E] border border-[#E8DDD3] hover:border-[#C4956A] hover:bg-[#C4956A]/5 cursor-pointer"
              }`}
            >
              {slot}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAF9F6] relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#C4956A]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
              Rendez-vous
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1A1A2E] mt-4">
              Prenez <span className="text-[#C4956A]">rendez-vous</span>
            </h1>
            <p className="text-lg text-[#1A1A2E]/60 mt-6 leading-relaxed">
              Choisissez un créneau qui vous convient pour un premier échange gratuit et sans engagement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-3 mb-12">
            {[
              { num: 1, label: "Date", icon: Calendar },
              { num: 2, label: "Heure", icon: Clock },
              { num: 3, label: "Informations", icon: Send },
            ].map(({ num, label, icon: Icon }) => (
              <React.Fragment key={num}>
                {num > 1 && (
                  <div
                    className={`w-12 h-0.5 ${
                      step >= num ? "bg-[#C4956A]" : "bg-[#E8DDD3]"
                    } transition-colors`}
                  />
                )}
                <button
                  onClick={() => {
                    if (num < step) setStep(num);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    step === num
                      ? "bg-[#C4956A] text-white"
                      : step > num
                      ? "bg-[#C4956A]/10 text-[#C4956A] cursor-pointer hover:bg-[#C4956A]/20"
                      : "bg-[#FAF9F6] text-[#1A1A2E]/30"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Calendar */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#FAF9F6] rounded-2xl p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => navigateMonth(-1)}
                  disabled={!canGoPrev()}
                  className={`p-2 rounded-lg transition-colors ${
                    canGoPrev()
                      ? "hover:bg-[#E8DDD3]/50 text-[#1A1A2E]"
                      : "text-[#1A1A2E]/20 cursor-not-allowed"
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-bold text-[#1A1A2E]">
                  {MOIS[currentMonth]} {currentYear}
                </h2>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 rounded-lg hover:bg-[#E8DDD3]/50 text-[#1A1A2E] transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {JOURS.map((j) => (
                  <div
                    key={j}
                    className="text-center text-xs font-semibold text-[#1A1A2E]/40 uppercase tracking-wider py-2"
                  >
                    {j}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>

              <p className="text-xs text-[#1A1A2E]/40 mt-4 text-center">
                Sélectionnez une date pour voir les créneaux disponibles
              </p>
            </motion.div>
          )}

          {/* Step 2: Time slots */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#FAF9F6] rounded-2xl p-6 lg:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setStep(1)}
                  className="p-2 rounded-lg hover:bg-[#E8DDD3]/50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-[#1A1A2E]" />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">
                    Choisissez un créneau
                  </h2>
                  <p className="text-sm text-[#C4956A] font-medium">
                    {formatDateFr(selectedDate)}
                  </p>
                </div>
              </div>

              {renderSlots()}

              <p className="text-xs text-[#1A1A2E]/40 mt-4 text-center">
                Les créneaux grisés sont indisponibles
              </p>
            </motion.div>
          )}

          {/* Step 3: Form */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#FAF9F6] rounded-2xl p-6 lg:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setStep(2)}
                  className="p-2 rounded-lg hover:bg-[#E8DDD3]/50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-[#1A1A2E]" />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">
                    Vos informations
                  </h2>
                  <p className="text-sm text-[#C4956A] font-medium">
                    {formatDateFr(selectedDate)} à {selectedSlot}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#1A1A2E]/60 mb-2 uppercase tracking-wider">
                    Message (optionnel)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-white border border-[#E8DDD3] rounded-lg text-sm text-[#1A1A2E] focus:outline-none focus:border-[#C4956A] focus:ring-1 focus:ring-[#C4956A] transition-colors resize-none"
                    placeholder="Décrivez brièvement votre projet..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300 hover:shadow-lg hover:shadow-[#C4956A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Envoi en cours..." : "Confirmer le rendez-vous"}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-[#FAF9F6] rounded-2xl p-8 lg:p-12 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#C4956A]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#C4956A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#1A1A2E]">
                Rendez-vous confirmé !
              </h2>
              <p className="text-[#1A1A2E]/60 mt-3 max-w-md mx-auto">
                Votre rendez-vous est prévu le{" "}
                <span className="font-semibold text-[#C4956A]">
                  {formatDateFr(selectedDate)}
                </span>{" "}
                à{" "}
                <span className="font-semibold text-[#C4956A]">
                  {selectedSlot}
                </span>
                .
              </p>
              <p className="text-[#1A1A2E]/60 mt-2">
                Un email de confirmation a été envoyé à{" "}
                <span className="font-medium">{formData.email}</span>.
              </p>
              <button
                onClick={reset}
                className="mt-8 px-8 py-3.5 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300 hover:shadow-lg hover:shadow-[#C4956A]/20"
              >
                Prendre un autre rendez-vous
              </button>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RendezVousPage;
